import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogIn, X, Phone, MessageCircle, Globe } from "lucide-react";
import { brandPhoneLink, whatsappLink } from "../lib/whatsapp";

const navItems = [
  { key: "home", to: "/" },
  { key: "plans", to: "/plans" },
  { key: "how", to: "/how-it-works" },
  { key: "faq", to: "/faq" },
  { key: "about", to: "/about" },
  { key: "blog", to: "/blog" },
  { key: "contact", to: "/contact" },
];

/**
 * Shared full-screen mobile menu drawer used by both <Navbar /> (top burger)
 * and <MobileStickyBar /> (scroll-down bar). Keeps both menus visually identical.
 */
export default function MobileMenuDrawer({ open, onClose, testidPrefix = "mmd" }) {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();

  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Close on route change
  useEffect(() => { if (open) onClose?.(); /* eslint-disable-next-line */ }, [pathname]);

  const lang = i18n.language?.startsWith("es") ? "es" : "en";
  const toggleLang = () => i18n.changeLanguage(lang === "es" ? "en" : "es");

  return (
    <div
      className={`md:hidden fixed inset-0 z-[60] transition-opacity duration-300 ${
        open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      }`}
      aria-hidden={!open}
    >
      <div
        className={`absolute inset-0 bg-white transition-transform duration-300 flex flex-col ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid={`${testidPrefix}-drawer`}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 border-b border-slate-100 shrink-0"
          style={{
            height: 64,
            paddingTop: "env(safe-area-inset-top)",
            boxSizing: "content-box",
          }}
        >
          <Link to="/" onClick={onClose} className="inline-flex items-center" aria-label="Velcor Energy">
            <img src="/velcor-logo.png" alt="Velcor" className="w-8 h-8 object-contain" />
          </Link>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            className="grid place-items-center w-10 h-10 rounded-full hover:bg-slate-100 active:scale-95 transition-all"
            data-testid={`${testidPrefix}-drawer-close`}
          >
            <X className="w-6 h-6 text-velcor-ink" strokeWidth={2.4} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="relative flex-1 overflow-y-auto z-10 flex flex-col">
          <div className="px-6 pt-6 pb-2">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 mb-2">
              {lang === "es" ? "Menú" : "Menu"}
            </div>
          </div>
          <nav className="px-3 pb-2">
            {navItems.map((it) => {
              const active = pathname === it.to;
              return (
                <Link
                  key={it.key}
                  to={it.to}
                  onClick={onClose}
                  className={`flex items-center justify-between px-3 py-4 rounded-lg text-[16px] font-bold transition-colors ${
                    active
                      ? "bg-velcor-green/10 text-velcor-green"
                      : "text-velcor-ink hover:bg-slate-50 active:bg-slate-100"
                  }`}
                  data-testid={`${testidPrefix}-nav-${it.key}`}
                >
                  {t(`nav.${it.key}`)}
                  <span className={`${active ? "text-velcor-green" : "text-slate-300"}`}>›</span>
                </Link>
              );
            })}
          </nav>

          <div className="px-6 mt-4 mb-6">
            <Link
              to="/portal/login"
              onClick={onClose}
              className="w-full h-12 rounded-full bg-velcor-ink text-white inline-flex items-center justify-center gap-2 font-extrabold text-[14px] active:scale-[0.98] transition-transform shadow-[0_8px_20px_rgba(15,29,61,0.20)]"
              data-testid={`${testidPrefix}-drawer-login`}
            >
              <LogIn className="w-4 h-4" strokeWidth={2.4} />
              {lang === "es" ? "Iniciar sesión" : "Sign in"}
            </Link>
          </div>

          <div className="flex-1" />

          <div
            className="px-3 pt-3 pb-6 border-t border-slate-100 bg-white/80 backdrop-blur-sm"
            style={{ paddingBottom: "max(24px, env(safe-area-inset-bottom))" }}
          >
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 px-3 mb-1.5">
              {lang === "es" ? "Soporte" : "Support"}
            </div>
            <a
              href={brandPhoneLink()}
              onClick={onClose}
              className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-50 active:bg-slate-100 text-velcor-ink"
              data-testid={`${testidPrefix}-drawer-phone`}
            >
              <span className="grid place-items-center w-9 h-9 rounded-full bg-velcor-green/10 text-velcor-green">
                <Phone className="w-4 h-4" strokeWidth={2.2} />
              </span>
              <div className="flex-1">
                <div className="text-[14px] font-bold tabular-nums">(800) 715-6516</div>
                <div className="text-[11.5px] text-slate-500 font-medium">
                  {lang === "es" ? "Llámanos · 7am – 9pm CT" : "Call us · 7am – 9pm CT"}
                </div>
              </div>
            </a>
            <a
              href={whatsappLink("Hola Velcor")}
              target="_blank"
              rel="noreferrer"
              onClick={onClose}
              className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-50 active:bg-slate-100 text-velcor-ink"
              data-testid={`${testidPrefix}-drawer-wa`}
            >
              <span className="grid place-items-center w-9 h-9 rounded-full bg-velcor-green/10 text-velcor-green">
                <MessageCircle className="w-4 h-4" strokeWidth={2.2} />
              </span>
              <div className="flex-1">
                <div className="text-[14px] font-bold">WhatsApp</div>
                <div className="text-[11.5px] text-slate-500 font-medium">
                  {lang === "es" ? "Respuesta < 5 min" : "Reply < 5 min"}
                </div>
              </div>
            </a>
            <button
              onClick={toggleLang}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-50 active:bg-slate-100 text-velcor-ink"
              data-testid={`${testidPrefix}-drawer-lang`}
            >
              <span className="grid place-items-center w-9 h-9 rounded-full bg-slate-100 text-slate-600">
                <Globe className="w-4 h-4" strokeWidth={2.2} />
              </span>
              <div className="flex-1 text-left">
                <div className="text-[14px] font-bold">
                  {lang === "es" ? "Idioma" : "Language"}
                </div>
                <div className="text-[11.5px] text-slate-500 font-medium">
                  {lang === "es" ? "Español" : "English"}
                </div>
              </div>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-velcor-green/10 text-velcor-green text-[10.5px] font-extrabold uppercase tracking-wider">
                {lang === "es" ? "ES → EN" : "EN → ES"}
              </span>
            </button>
          </div>

          {/* Footer block — mirrors the desktop footer signature so the
              customer always sees the company name and PUCT reference even
              in the mobile drawer. */}
          <div className="mt-6 pt-4 border-t border-slate-200 text-center space-y-1">
            <div className="text-[11px] text-slate-500 font-bold leading-snug">
              © {new Date().getFullYear()} Velcor Energy LLC
            </div>
            <div className="text-[10.5px] text-slate-400 leading-snug">
              Houston, TX · USA · PUCT REP# Pending
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
